import dayjs from 'dayjs';

import { DATE_KEY_FORMAT } from '@/helpers';

import {
  BUFFERED_PERIOD_MONTHS,
  HORIZONTAL_WINDOW_EDGE_BUFFER_DAYS,
  HORIZONTAL_WINDOW_SHIFT_DAYS,
  STALE_SCROLL_THRESHOLD_MONTHS,
} from '../../constants/sizes';
import { calculateBufferedPeriod, getPlannerDatesWindow } from '../../helpers/date';
import { QueryDateRangeMap, ScrollToDateTriggerMap } from '../types';

/**
 * Pure decision helpers for date navigation (arrows, today button, date picker):
 * "is the target rendered? near an edge? too far from the viewport to trust
 * cached pages?" — plus the state slices those decisions produce.
 */

/**
 * Navigation must recentre the fixed window before the target gets too close to either
 * edge; otherwise arrow/date-picker/today navigation can land in a viewport with empty
 * columns because the scroll target outruns the currently rendered date window.
 */
export const NAVIGATION_EDGE_GUARD_DAYS =
  HORIZONTAL_WINDOW_SHIFT_DAYS + HORIZONTAL_WINDOW_EDGE_BUFFER_DAYS;

export const getNavigationTargetState = (
  windowDates: dayjs.Dayjs[],
  targetDate: dayjs.Dayjs
): { targetExists: boolean; shouldRegenerateDates: boolean } => {
  const targetIndex = windowDates.findIndex(date => date.isSame(targetDate, 'day'));

  if (targetIndex === -1) {
    return {
      targetExists: false,
      shouldRegenerateDates: true,
    };
  }

  const shouldRegenerateDates =
    targetIndex <= NAVIGATION_EDGE_GUARD_DAYS ||
    targetIndex >= windowDates.length - NAVIGATION_EDGE_GUARD_DAYS;

  return {
    targetExists: true,
    shouldRegenerateDates,
  };
};

/** Returns true when the target is too far from the current viewport to trust cached data pages. */
export const isStaleScrollDistance = (
  firstVisibleDate: dayjs.Dayjs | null,
  targetDate: dayjs.Dayjs
): boolean =>
  !!firstVisibleDate &&
  Math.abs(firstVisibleDate.diff(targetDate, 'month', true)) > STALE_SCROLL_THRESHOLD_MONTHS;

/** State slice that recentres the dates window on a target and bumps the regeneration trigger. */
export const recenterDatesWindow = (targetDate: dayjs.Dayjs, datesRegenerationTrigger: number) => ({
  dates: getPlannerDatesWindow(targetDate),
  datesRegenerationTrigger: datesRegenerationTrigger + 1,
});

interface DateReloadStateInput {
  groupBy: keyof QueryDateRangeMap;
  queryDateRanges: QueryDateRangeMap;
  scrollToDateTriggers: ScrollToDateTriggerMap;
  datesRegenerationTrigger: number;
}

/**
 * Builds the state slice that reloads data centered on targetDate and queues a scroll.
 * Used when the target is in the loaded range but dates need to be regenerated (too close to
 * the edge) or the cache is stale (user scrolled far away).
 */
export const buildDateReloadState = (state: DateReloadStateInput, targetDate: dayjs.Dayjs) => {
  const dateStr = targetDate.format(DATE_KEY_FORMAT);
  const activeGroupBy = state.groupBy;
  const bufferedPeriod = calculateBufferedPeriod(dateStr, dateStr, BUFFERED_PERIOD_MONTHS);

  return {
    dates: getPlannerDatesWindow(targetDate),
    queryDateRanges: {
      ...state.queryDateRanges,
      [activeGroupBy]: bufferedPeriod,
    },
    scrollToDateTriggers: {
      ...state.scrollToDateTriggers,
      [activeGroupBy]: state.scrollToDateTriggers[activeGroupBy] + 1,
    },
    datesRegenerationTrigger: state.datesRegenerationTrigger + 1,
    pendingScrollTarget: targetDate,
  };
};
